'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Box, Popover, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapboxMap,
  MapProvider,
  MapboxSelectionProvider,
  useMapboxSelection,
  FloatingCard,
  FloatingBounce,
  MarkerSprite,
  GlassPanel,
  ConnectedButtonGroup,
  IconButton as DSIconButton,
  Button as DSButton,
  useFloatingCard,
  useSupportsLiquidGlass,
} from '@miso/design-system';
import type { MapMarker, MapArrow, MapCamera } from '@miso/design-system';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import type { TabPageComponent, TabContext } from '@/entities/tab/model/tab.types';
import { TabToolbar } from '@/widgets/tab/tab-toolbar';
import { TabAwarePageWrapper } from '@/shared/ui/overlay/Modal';
import { useAuth, useAuthSyncState } from '@/entities/auth/model/useAuth';
import { useAtomValue } from 'jotai';
import { viewModeAtom } from '@/entities/workspace-config';
import { useMyPlanner } from '@/features/planner/get-planner/useMyPlanner';
import { usePlannerScheduleDataByMe } from '@/widgets/planner-for-planner';
import { useMyPlannerTodayWorkStatus } from '@/entities/planner/model/useMyPlannerTodayWorkStatus';
import { shouldDisableDateInPlannerCalendar } from '@/entities/planner/model/plannerCalendarDisable';
import { useDockviewApi } from '@/app/providers/DockviewApiProvider';
import { useNavigateTab } from '@/features/tab/tab-history';
import { useUpdateTabParams } from '@/features/tab/tab-history';
import { deserialize } from '@/entities/tab/model/path';
import { useSetAtom } from 'jotai';
import { suppressWorkspaceActionFabAtom } from '@/shared/lib/suppressWorkspaceActionFabAtom';
import { Fab, Menu } from '@miso/design-system';
import {
  ThemeSwitcherSubMenu,
  LogoutMenuItem,
  ViewModeSwitcherSubMenu,
} from '@/shared/ui/fab/menu-items';
import type { PlannerDayCounselByPlanner } from '@/entities/visit-counsel/model/usePlannerDayCounselsByPlanner';
import MenuIcon from '@mui/icons-material/Menu';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useMyVisitCounselDetail } from '@/entities/visit-counsel';
import type { MyVisitCounselDetailData } from '@/entities/visit-counsel/model/useMyVisitCounselDetail';
import {
  formatStructuredAddressTwoLine,
  VisitCounselState,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { getIntakeDepartureAddress } from '@/entities/visit-counsel/model/intake-helpers';
import { formatScheduleTimeSingle } from '@/shared/lib/timeslot/scheduleTimeRangeFormatter';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { formatPhoneNumber, toTelHref } from '@/shared/lib/formatPhone';
import { Toast, useToast } from '@/shared/ui/feedback/Toast';
import { VisitReportSheet } from '@/features/visit-counsel/visit-report';

dayjs.locale('ko');

const MOBILE_BREAKPOINT = 600;
const HEADER_HEIGHT = 56;

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 18l6-6-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v16c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H5V8h14v13z" />
    </svg>
  );
}

// ── Time formatter ────────────────────────────────────────────────────────────

/**
 * scheduleTime → 단일 시작 시각 (서버 시간 - 30분)
 * 예) "10:00" → "오전 9시 30분", "22:00" → "오후 9시 30분"
 */
const formatScheduleTime = formatScheduleTimeSingle;

function formatMoveDate(date: string) {
  return dayjs(date).format('YYYY년 M월 D일 (ddd)');
}

/**
 * detail 데이터 + preview counsel 데이터를 합쳐서 카드에 표시할 정규화된 정보를 반환.
 * detail이 아직 없으면 preview counsel 데이터로 fallback (즉시 표시 가능).
 */
function deriveDetailCardInfo(
  counsel: PlannerDayCounselByPlanner,
  detail: MyVisitCounselDetailData | null
) {
  const detailCounsel = detail?.counsel;
  const depAddr = detailCounsel ? getIntakeDepartureAddress(detailCounsel) : null;
  const isStarted = detailCounsel?.state === VisitCounselState.STARTED;

  const addressLines = (() => {
    if (depAddr) {
      const { primaryLine, secondaryLine } = formatStructuredAddressTwoLine(depAddr);
      return { line1: primaryLine, line2: secondaryLine || undefined };
    }
    const road = counsel.location?.roadAddress;
    if (road) return { line1: road, line2: undefined };
    return { line1: '주소 정보 없음', line2: undefined };
  })();

  const roadAddressForCopy = (() => {
    if (depAddr) return formatStructuredAddressTwoLine(depAddr).primaryLine;
    return counsel.location?.roadAddress ?? '';
  })();

  const customerPhone = detail?.customer?.phone ?? detail?.order?.ordererPhone ?? '';
  const moveDate = detail?.order?.moving?.moveDate ?? '';
  const arrivalAddress = detail?.order?.moving?.arrival?.location ?? '';
  const arrivalSupplyArea = detail?.order?.moving?.arrival?.supplyArea ?? 0;
  const supplyArea = detail?.counsel.supplyArea ?? counsel.supplyArea;

  const appointedEvent = detail?.events.events.find((e) => e.event?.eventType === 'Appointed');
  const appointedAt = appointedEvent?.event.occurredAt;

  return {
    addressLines,
    roadAddressForCopy,
    customerPhone,
    moveDate,
    arrivalAddress,
    arrivalSupplyArea,
    supplyArea,
    isStarted,
    appointedAt,
    hasAppointedEvent: !!appointedEvent,
  };
}

// ── FloatingCard Contents ─────────────────────────────────────────────────────

interface MapCardContentsProps {
  /** preview/detail 공통: 마커가 가리키는 상담 (목록 데이터) */
  counsel: PlannerDayCounselByPlanner;
  /** 'detail' 모드일 때 사용할 상세 데이터 (로딩 중이면 null/일부만 채워질 수 있음) */
  detail: MyVisitCounselDetailData | null;
  /** 'preview' = 마커 클릭 직후 / 'detail' = 상담 상세 morph 상태 */
  mode: 'preview' | 'detail';
  onCollapse: () => void;
  /** preview 모드에서 "상담 보기" 또는 마커 chevron 클릭 시 */
  onViewDetails: () => void;
  /** detail 모드에서 "상담 시작/이어하기" 클릭 시 */
  onStartOrProgress: () => void;
  /** detail 모드 클립보드 복사 */
  onCopy: (text: string, label: string) => void;
  /** detail 모드에서 "방문 보고" 클릭 시 */
  onOpenReport: () => void;
}

/**
 * 동일한 FloatingCard 내에서 mode에 따라 컨텐츠가 morph 한다.
 *
 * - preview: 시간 + 주소 (collapsed) / 평수·태그 (expanded), CTA "상담 보기"
 * - detail: 풀 헤더 + 이사 정보·이력 섹션 + 전화/주소 복사 + "상담 시작/이어하기"
 *
 * 카드 자체는 한 번만 마운트되고, 내부 슬롯만 교체되므로
 * framer-motion의 `layout`이 부드럽게 높이를 보간해 "카드가 커지는" 느낌을 만든다.
 */
function MapCardContents({
  counsel,
  detail,
  mode,
  onCollapse,
  onViewDetails,
  onStartOrProgress,
  onCopy,
  onOpenReport,
}: MapCardContentsProps) {
  const { isExpanded } = useFloatingCard();
  const formattedTime = formatScheduleTime(counsel.scheduleTime);

  if (mode === 'preview') {
    const address = counsel.location?.roadAddress ?? '주소 정보 없음';
    const isStarted = counsel.state === 'STARTED';

    return (
      <>
        <FloatingCard.CollapseButton onPress={onCollapse} />
        <FloatingCard.Body>
          <FloatingCard.Header>
            <FloatingCard.WhenCollapsed>
              <FloatingCard.Eyebrow>{formattedTime}</FloatingCard.Eyebrow>
              <FloatingCard.Title>{address}</FloatingCard.Title>
            </FloatingCard.WhenCollapsed>

            <FloatingCard.WhenExpanded>
              <FloatingCard.TitleRow>
                <FloatingCard.Title>{address}</FloatingCard.Title>
                <FloatingCard.ExpandChevron />
              </FloatingCard.TitleRow>
              {(counsel.supplyArea != null || isStarted) && (
                <FloatingCard.TagGroup>
                  {counsel.supplyArea != null && (
                    <FloatingCard.Tag>{counsel.supplyArea}평</FloatingCard.Tag>
                  )}
                  {isStarted && <FloatingCard.Tag>진행중</FloatingCard.Tag>}
                </FloatingCard.TagGroup>
              )}
            </FloatingCard.WhenExpanded>
          </FloatingCard.Header>

          <FloatingCard.Details>
            <FloatingCard.Section>
              <FloatingCard.SectionLabel>방문 정보</FloatingCard.SectionLabel>
              <FloatingCard.SectionContent>
                <FloatingCard.Row>{formattedTime} 방문</FloatingCard.Row>
                {counsel.misoOrderId && (
                  <FloatingCard.Row>주문번호 #{counsel.misoOrderId}</FloatingCard.Row>
                )}
              </FloatingCard.SectionContent>
            </FloatingCard.Section>
          </FloatingCard.Details>

          <FloatingCard.ActionBar>
            <FloatingCard.MainCTA>
              <DSButton variant="primary" size="s" onPress={onViewDetails}>
                {isExpanded ? '상담 보기' : '상담 보기'}
              </DSButton>
            </FloatingCard.MainCTA>
          </FloatingCard.ActionBar>
        </FloatingCard.Body>
      </>
    );
  }

  // ── detail mode ──────────────────────────────────────────────────────────
  const info = deriveDetailCardInfo(counsel, detail);
  const ctaLabel = info.isStarted ? '이어하기' : '상담 시작';

  return (
    <>
      <FloatingCard.CollapseButton onPress={onCollapse} />
      <FloatingCard.Body>
        <FloatingCard.Header>
          <FloatingCard.Eyebrow>{formattedTime}</FloatingCard.Eyebrow>
          <FloatingCard.TitleRow>
            <FloatingCard.Title>{info.addressLines.line1}</FloatingCard.Title>
            <FloatingCard.ExpandChevron />
          </FloatingCard.TitleRow>
          {info.addressLines.line2 && (
            <FloatingCard.Subtitle>{info.addressLines.line2}</FloatingCard.Subtitle>
          )}
          {(info.supplyArea != null || info.isStarted) && (
            <FloatingCard.TagGroup>
              {info.supplyArea != null && <FloatingCard.Tag>{info.supplyArea}평</FloatingCard.Tag>}
              {info.isStarted && <FloatingCard.Tag>진행중</FloatingCard.Tag>}
            </FloatingCard.TagGroup>
          )}
        </FloatingCard.Header>

        <FloatingCard.Details>
          <FloatingCard.Section>
            <FloatingCard.SectionLabel>이사 정보</FloatingCard.SectionLabel>
            <FloatingCard.SectionContent>
              <FloatingCard.Row>
                {info.moveDate ? formatMoveDate(info.moveDate) : '-'}
              </FloatingCard.Row>
              <FloatingCard.Row>
                {info.arrivalAddress
                  ? `${info.arrivalAddress}${info.arrivalSupplyArea ? ` (${info.arrivalSupplyArea}평)` : ''}`
                  : '-'}
              </FloatingCard.Row>
            </FloatingCard.SectionContent>
          </FloatingCard.Section>
          {info.hasAppointedEvent && (
            <FloatingCard.Section>
              <FloatingCard.SectionLabel>이력</FloatingCard.SectionLabel>
              <FloatingCard.SectionContent>
                <FloatingCard.Row>
                  상담 배정 {info.appointedAt ? dayjs(info.appointedAt).format('M/D HH:mm') : ''}
                </FloatingCard.Row>
              </FloatingCard.SectionContent>
            </FloatingCard.Section>
          )}
        </FloatingCard.Details>

        <FloatingCard.ActionBar>
          <FloatingCard.SubButtonGroup>
            {info.customerPhone && toTelHref(info.customerPhone) && (
              <DSIconButton
                variant="default"
                size="m"
                icon={<LocalPhoneOutlinedIcon />}
                aria-label={`통화 ${formatPhoneNumber(info.customerPhone)}`}
                onPress={() => {
                  const href = toTelHref(info.customerPhone);
                  if (href) window.location.href = href;
                }}
              />
            )}
            <DSIconButton
              variant="default"
              size="m"
              icon={<ContentCopyOutlinedIcon />}
              aria-label="주소 복사"
              onPress={() => onCopy(info.roadAddressForCopy, '주소')}
            />
            <DSIconButton
              variant="default"
              size="m"
              icon={<MapIcon />}
              aria-label="네이버 지도에서 보기"
              onPress={() => {
                const query = [info.addressLines.line1, info.addressLines.line2]
                  .filter(Boolean)
                  .join(' ');
                if (!query) return;
                window.open(
                  `https://map.naver.com/p/search/${encodeURIComponent(query)}`,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            />
            <DSIconButton
              variant="default"
              size="m"
              icon={<ReportProblemOutlinedIcon />}
              aria-label="방문 보고"
              onPress={onOpenReport}
            />
          </FloatingCard.SubButtonGroup>
          <FloatingCard.MainCTA>
            <DSButton variant="primary" size="s" onPress={onStartOrProgress} disabled={!detail}>
              {ctaLabel}
            </DSButton>
          </FloatingCard.MainCTA>
        </FloatingCard.ActionBar>
      </FloatingCard.Body>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseScheduleDate(value: unknown): string {
  if (typeof value !== 'string') return dayjs().format('YYYY-MM-DD');
  const d = dayjs(value, 'YYYY-MM-DD', true);
  return d.isValid() ? d.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
}

function formatDateTitle(dateStr: string): string {
  const d = dayjs(dateStr);
  const isCurrentYear = d.year() === dayjs().year();
  return isCurrentYear ? d.format('M월 D일') : d.format('YYYY년 M월 D일');
}

// ── Inner body — selection-aware ──────────────────────────────────────────────

interface PlannerMapBodyProps {
  isMobile: boolean;
  headerTitle: string;
  onPrevDay: () => void;
  onNextDay: () => void;
  onDateSelect: (date: string) => void;
  scheduleDate: string;
  workDates: string[];
  isWorkDatesLoaded: boolean;
  todayDateString: string;
  todayWorkStatus: 'work' | 'off' | null;
  markers: MapMarker[];
  arrows: MapArrow[];
  /** detail 카드의 "상담 시작" / "이어하기" 버튼 클릭 시 (실제 라우팅) */
  onNavigateToStartOrProgress: (counselId: string, isStarted: boolean) => void;
  onNavigateToProfile: () => void;
  onNavigateToCounselList: () => void;
  onNavigateToCameraSettings: () => void;
  /** 목록 등 외부 진입 시 자동 선택할 상담 ID */
  initialVisitCounselId?: string | null;
  /** deep-link 선택 적용 후 URL 정리 */
  onInitialVisitCounselIdApplied?: () => void;
  /** deep-link 진입 시 '상담 보기' detail 카드로 바로 연다 */
  initialOpenDetail?: boolean;
}

/**
 * `<MapboxSelectionProvider>` 안쪽에서 동작하는 본문 컴포넌트.
 *
 * ## 3-레이어 morph 트랜지션
 *
 * 헤더 / 지도 / 플로팅카드 — 세 레이어가 한 번만 마운트되고, 선택·detail 모드
 * 변화에 따라 각 레이어 안에서 변경된 부분만 리렌더링한다. 핀 클릭 → 상담 상세는
 * 별도 페이지 라우팅 없이 같은 카드가 morph(높이/내용)되어 자연스럽게 이어진다.
 *
 * - **헤더**: 카드 표시 여부에 따라 살짝 fade. 텍스트 자체는 유지(스왑 깜빡임 방지).
 * - **지도**: detail 모드에서 markers를 선택된 1건으로 좁히고 fitKey만 바꿔서
 *   카메라가 해당 마커로 줌인. MapboxMap 인스턴스는 유지된다.
 * - **플로팅카드**: AnimatePresence + motion.div(layout) 로 카드는 한 번만 마운트되고
 *   내부 슬롯만 `mode='preview' | 'detail'` 로 바뀐다. framer-motion layout 보간이
 *   높이/위치를 부드럽게 이어붙여 "카드가 커지는" 인상을 만든다.
 */
function PlannerMapBody({
  isMobile,
  headerTitle,
  onPrevDay,
  onNextDay,
  onDateSelect,
  scheduleDate,
  workDates,
  isWorkDatesLoaded,
  todayDateString,
  todayWorkStatus,
  markers,
  arrows,
  counsels,
  onNavigateToStartOrProgress,
  onNavigateToProfile,
  onNavigateToCounselList,
  onNavigateToCameraSettings,
  initialVisitCounselId = null,
  onInitialVisitCounselIdApplied,
  initialOpenDetail = false,
}: PlannerMapBodyProps & { counsels: PlannerDayCounselByPlanner[] }) {
  const { selectedMarkerId, select, unselect } = useMapboxSelection();
  const [isExpanded, setIsExpanded] = useState(false);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
  const [detailVisitCounselId, setDetailVisitCounselId] = useState<string | null>(null);
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const navGroupRef = useRef<HTMLDivElement>(null);
  const appliedDeepLinkRef = useRef<string | null>(null);
  // SVG backdrop-filter 미지원(Safari/Firefox 등) → 솔리드 캡슐로 폴백.
  const supportsLiquidGlass = useSupportsLiquidGlass();

  const selectedCounsel = useMemo(
    () => counsels.find((c) => c.visitCounselId === selectedMarkerId) ?? null,
    [counsels, selectedMarkerId]
  );

  // 목록 등에서 넘어온 visitCounselId deep-link → 마커 선택 (+ optional detail 카드)
  useEffect(() => {
    if (!initialVisitCounselId) {
      appliedDeepLinkRef.current = null;
      return;
    }
    if (appliedDeepLinkRef.current === initialVisitCounselId) return;

    const counsel = counsels.find((c) => c.visitCounselId === initialVisitCounselId);
    const hasMarker = markers.some((m) => m.id === initialVisitCounselId);
    if (!counsel || !hasMarker) return;

    select(initialVisitCounselId);
    if (initialOpenDetail) {
      setDetailVisitCounselId(initialVisitCounselId);
      setIsExpanded(true);
    }
    appliedDeepLinkRef.current = initialVisitCounselId;
    onInitialVisitCounselIdApplied?.();
  }, [
    initialVisitCounselId,
    initialOpenDetail,
    counsels,
    markers,
    select,
    onInitialVisitCounselIdApplied,
  ]);

  // 선택이 해제(자동/수동)되면 expanded·detail 상태도 함께 리셋해 다음 카드 등장 시
  // 항상 preview-collapsed 부터 시작하도록.
  useEffect(() => {
    if (selectedMarkerId == null) {
      setIsExpanded(false);
      setDetailVisitCounselId(null);
    }
  }, [selectedMarkerId]);

  // 다른 마커로 선택이 옮겨가면 (detail이 떠있던 도중 다른 핀 클릭 등)
  // 이전 detail은 자동으로 닫고 preview 모드로 회귀.
  useEffect(() => {
    if (detailVisitCounselId && selectedMarkerId && detailVisitCounselId !== selectedMarkerId) {
      setDetailVisitCounselId(null);
    }
  }, [selectedMarkerId, detailVisitCounselId]);

  // detail 모드일 때만 상세 데이터를 fetch (preview는 목록 데이터로 충분).
  const { data: counselDetail } = useMyVisitCounselDetail(
    detailVisitCounselId,
    !!detailVisitCounselId
  );

  const isDetailMode = detailVisitCounselId != null;
  const cardMode: 'preview' | 'detail' = isDetailMode ? 'detail' : 'preview';

  const handleCollapseCard = useCallback(() => {
    if (isDetailMode) {
      // detail → preview(collapsed) 로 morph. 선택 마커는 유지하되, 펼침 상태도 함께
      // 풀어서 "마커 클릭 직후"와 동일한 collapsed preview(시간 + 주소 + CTA) 로 회귀.
      setDetailVisitCounselId(null);
      setIsExpanded(false);
      return;
    }
    unselect();
    setIsExpanded(false);
  }, [isDetailMode, unselect]);

  const handleEnterDetail = useCallback(() => {
    if (!selectedCounsel) return;
    setDetailVisitCounselId(selectedCounsel.visitCounselId);
    // detail 진입 시 항상 expanded 상태로 시작 (이사 정보·이력 섹션 바로 노출)
    setIsExpanded(true);
  }, [selectedCounsel]);

  const handleStartOrProgress = useCallback(() => {
    if (!selectedCounsel) return;
    const isStarted = counselDetail?.counsel.state === VisitCounselState.STARTED;
    onNavigateToStartOrProgress(selectedCounsel.visitCounselId, isStarted);
  }, [selectedCounsel, counselDetail, onNavigateToStartOrProgress]);

  const handleCopy = useCallback(
    async (text: string, label: string) => {
      const ok = await copyToClipboard(text);
      showToast(ok ? `${label} 복사됨` : '복사 실패', ok ? 'success' : 'error');
    },
    [showToast]
  );

  const cardVisible = selectedMarkerId != null;

  // detail 모드: 모든 마커·경로 유지 + 선택 상담 좌표로 카메라 줌인
  const cameraConfig = useMemo<MapCamera>(() => {
    if (isDetailMode && selectedMarkerId) {
      const focused = markers.find((m) => m.id === selectedMarkerId);
      if (focused) {
        return {
          autoFit: false,
          focus: focused.position,
          focusZoom: 16,
          fitKey: `detail:${selectedMarkerId}`,
        };
      }
    }
    return {
      autoFit: true,
      fitKey: scheduleDate,
    };
  }, [isDetailMode, selectedMarkerId, markers, scheduleDate]);

  // 헤더 타이틀: detail 모드에서는 상담 시간 + 주소로 morph (preview/idle은 날짜)
  const detailHeaderTitle = useMemo(() => {
    if (!isDetailMode || !selectedCounsel) return null;
    const time = formatScheduleTime(selectedCounsel.scheduleTime);
    const addr = selectedCounsel.location?.roadAddress ?? '';
    return addr ? `${time} · ${addr}` : time;
  }, [isDetailMode, selectedCounsel]);
  const displayHeaderTitle = detailHeaderTitle ?? headerTitle;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Header 레이어 (모바일) ─────────────────────────────────
          한 번만 마운트되고 텍스트만 morph 한다. detail 모드일 때 상담 시간/주소로
          타이틀이 부드럽게 교체되고, motion.span layout이 폭 변화를 보간한다. */}
      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            height: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            borderBottom: '1px solid #f3f4f6',
            bgcolor: 'white',
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={displayHeaderTitle}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
              style={{
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-0.034em',
                color: '#5f5f5f',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {displayHeaderTitle}
            </motion.span>
          </AnimatePresence>
        </Box>
      )}

      {/* ── Map 레이어 ─────────────────────────────────────────────
          MapboxMap 인스턴스는 한 번만 마운트된다. detail 모드 진입 시
          markers를 선택된 1건으로 좁히고 fitKey만 바꾸면 카메라가 그 마커로
          줌인되어 단일 위치 정보 지도처럼 보인다. */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? HEADER_HEIGHT : 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <MapboxMap
          height="100%"
          minHeight={200}
          markers={markers}
          arrows={arrows}
          camera={cameraConfig}
          aria-label="일별 상담 지도"
          activeMarkerId={selectedMarkerId}
          onActiveMarkerChange={select}
          renderActiveMarker={(m) => (
            <FloatingBounce>
              <MarkerSprite marker={m} />
            </FloatingBounce>
          )}
        />
      </Box>

      {/* ── 날짜 네비게이션 + FAB — 우측 하단 ─────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          right: 16,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          opacity: cardVisible ? 0 : 1,
          pointerEvents: cardVisible ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        }}
      >
        {/*
         * Liquid Glass(SVG backdrop-filter) 미지원 환경(Safari/Firefox 등)에서는
         * 글래스가 단색 흰 박스로 어색하게 떨어지기 때문에, 아예 솔리드 흰 캡슐 +
         * default 버튼으로 폴백한다.
         *
         * 글래스 환경에서는 ghost 버튼의 기본 hover/active 색(`bg-gray-100`)이
         * 글래스 위에서 솔리드 회색 박스로 튀어 보이므로, 반투명 흰색으로 override.
         */}
        {(() => {
          const isGlass = supportsLiquidGlass;
          // Tailwind v4 important modifier: suffix `!` (prefix `!`는 deprecated).
          // ghost variant의 `hover:bg-gray-100`을 글래스 친화적인 반투명 흰색으로 override.
          // `:active`(터치/클릭)도 함께 처리해 모바일에서 회색 잔상 방지.
          // react-aria는 pressed 시 `data-pressed="true"`도 부여하므로 함께 매칭.
          const buttonClassName = isGlass
            ? 'bg-transparent! hover:bg-white/25! active:bg-white/35! data-[pressed=true]:bg-white/35!'
            : undefined;
          const buttons = (
            <Box ref={navGroupRef} sx={{ display: 'inline-flex' }}>
              <ConnectedButtonGroup gap="md">
                <DSIconButton
                  variant={isGlass ? 'ghost' : 'default'}
                  size="xl"
                  className={buttonClassName}
                  icon={<ChevronLeftIcon />}
                  aria-label="이전 날짜"
                  onPress={onPrevDay}
                />
                <DSIconButton
                  variant={isGlass ? 'ghost' : 'default'}
                  size="xl"
                  className={buttonClassName}
                  icon={<CalendarIcon />}
                  aria-label="날짜 선택"
                  onPress={() => setCalendarAnchorEl(navGroupRef.current)}
                />
                <DSIconButton
                  variant={isGlass ? 'ghost' : 'default'}
                  size="xl"
                  className={buttonClassName}
                  icon={<ChevronRightIcon />}
                  aria-label="다음 날짜"
                  onPress={onNextDay}
                />
              </ConnectedButtonGroup>
            </Box>
          );

          if (isGlass) {
            return (
              <GlassPanel
                surface="squircle"
                radius={32}
                tint="rgba(255, 255, 255, 0.22)"
                bezelWidth={14}
                glassThickness={11}
                refraction={0.9}
                specularOpacity={0.35}
                fallbackBlur={20}
                innerBlur={10}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 6,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 14px rgba(0,0,0,0.15)',
                }}
              >
                {buttons}
              </GlassPanel>
            );
          }

          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px',
                borderRadius: '32px',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 14px rgba(0,0,0,0.12)',
              }}
            >
              {buttons}
            </Box>
          );
        })()}
        <Fab icon={<MenuIcon />} aria-label="메뉴" placement="top-end">
          <Menu.Section label="플래너">
            <Menu.Item icon={<MapIcon fontSize="small" />} disabled>
              상담 지도
            </Menu.Item>
            <Menu.Item icon={<EventNoteIcon fontSize="small" />} onClick={onNavigateToCounselList}>
              상담 목록
            </Menu.Item>
            <Menu.Item icon={<PersonIcon fontSize="small" />} onClick={onNavigateToProfile}>
              내 정보
            </Menu.Item>
            <Menu.Item
              icon={<VideocamIcon fontSize="small" />}
              onClick={onNavigateToCameraSettings}
            >
              카메라 설정
            </Menu.Item>
          </Menu.Section>
          <Menu.Section label="설정">
            <ViewModeSwitcherSubMenu />
            <ThemeSwitcherSubMenu />
            <LogoutMenuItem />
          </Menu.Section>
        </Fab>
      </Box>

      {/* ── 날짜 선택 캘린더 팝오버 ─────────────────────────────── */}
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={() => setCalendarAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableScrollLock
        slotProps={{
          paper: { sx: { borderRadius: 3, mt: -1, boxShadow: 4 } },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <DateCalendar
            value={dayjs(scheduleDate)}
            onChange={(newDate) => {
              if (newDate) {
                onDateSelect(newDate.format('YYYY-MM-DD'));
                setCalendarAnchorEl(null);
              }
            }}
            shouldDisableDate={(date) =>
              shouldDisableDateInPlannerCalendar(date.format('YYYY-MM-DD'), {
                workDates,
                isWorkDatesLoaded,
                todayDateString,
                todayWorkStatus,
              })
            }
          />
        </LocalizationProvider>
      </Popover>

      {/* ── FloatingCard 레이어 (마커 선택 시) ─────────────────────
          AnimatePresence 안의 motion.div는 key="floating-card" 한 개만 — preview ↔
          detail 모드 전환은 카드 안 슬롯만 교체하므로 카드 자체는 마운트 유지된다.
          motion.div의 layout prop이 mode 전환에 따른 높이 변화를 spring으로 보간해서
          "카드가 커지는" 모핑 인상을 만든다. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          padding: '20px 12px 24px',
          maxHeight: 'calc(100% - 76px)',
          overflow: 'auto',
          display: 'flex',
          justifyContent: isExpanded ? 'stretch' : 'flex-end',
          pointerEvents: 'none',
        }}
      >
        <AnimatePresence initial={false}>
          {selectedCounsel && (
            <motion.div
              key="floating-card"
              layout
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{
                layout: { duration: 0.32, ease: [0.32, 0.72, 0, 1] },
                default: { duration: 0.22, ease: [0.32, 0.72, 0, 1] },
              }}
              style={{
                pointerEvents: 'auto',
                width: isExpanded ? '100%' : 'auto',
                maxWidth: '100%',
              }}
            >
              <FloatingCard
                variant="glass"
                expanded={isDetailMode ? isExpanded : isExpanded}
                onExpandedChange={setIsExpanded}
              >
                <MapCardContents
                  counsel={selectedCounsel}
                  detail={counselDetail ?? null}
                  mode={cardMode}
                  onCollapse={handleCollapseCard}
                  onViewDetails={handleEnterDetail}
                  onStartOrProgress={handleStartOrProgress}
                  onCopy={handleCopy}
                  onOpenReport={() => setIsReportSheetOpen(true)}
                />
              </FloatingCard>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {selectedCounsel && (
        <VisitReportSheet
          open={isReportSheetOpen}
          onClose={() => setIsReportSheetOpen(false)}
          visitCounselId={selectedCounsel.visitCounselId}
          isMobile={isMobile}
          onSubmitted={() =>
            showToast('보고가 접수되었습니다. 운영팀이 확인 후 처리합니다.', 'success')
          }
        />
      )}

      <Toast
        open={!!toast}
        message={toast?.message ?? ''}
        severity={toast?.severity ?? 'neutral'}
        autoHideDuration={2000}
        onClose={hideToast}
        position="bottom"
        horizontal="center"
      />
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const PlannerPage: TabPageComponent = (props: TabContext) => {
  const { user } = useAuth();
  const { isLoading: isAuthLoading, isInitialized: isAuthInitialized } = useAuthSyncState();
  const viewMode = useAtomValue(viewModeAtom);
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  const plannerId = user?.plannerId ?? '';
  const isPlannerMode = user?.role === 'Planner' || viewMode === 'Planner';

  const { planner, isLoading: plannerLoading } = useMyPlanner(
    isAuthInitialized && !isAuthLoading && !!plannerId && isPlannerMode
  );

  const api = useDockviewApi();
  const { navigateTab } = useNavigateTab(api);
  const { updateTabParams } = useUpdateTabParams();

  const scheduleDate = parseScheduleDate(
    props.params?.scheduleDate ?? searchParams?.get('scheduleDate')
  );

  const initialVisitCounselId =
    (typeof props.params?.visitCounselId === 'string' ? props.params.visitCounselId : null) ??
    searchParams?.get('visitCounselId') ??
    null;

  const initialOpenDetail =
    props.params?.cardMode === 'detail' || searchParams?.get('cardMode') === 'detail';

  const handleInitialVisitCounselIdApplied = useCallback(() => {
    if (!props.tabId || !initialVisitCounselId) return;
    updateTabParams(props.tabId, { visitCounselId: undefined, cardMode: undefined });
  }, [props.tabId, initialVisitCounselId, updateTabParams]);

  const plannerScheduleData = usePlannerScheduleDataByMe(scheduleDate, plannerId);
  const { counsels, workDates, isWorkDatesLoaded } = plannerScheduleData;

  /**
   * 첫 페인트 시점에 캘린더가 휴무/근무 마킹을 비워둔 채 보이지 않도록 (버그 1),
   * "오늘"의 근무 상태만 별도로 빠르게 받아 fallback 으로 사용한다.
   * 다른 날짜는 가짜로 채우지 않는다.
   */
  const { todayDateString, todayWorkStatus } = useMyPlannerTodayWorkStatus(
    isAuthInitialized && !isAuthLoading && !!plannerId && isPlannerMode
  );

  const handleNavigateToProfile = useCallback(() => {
    const context = deserialize('/workspace/my/profile');
    const activePanel = api?.activePanel;
    if (activePanel) {
      const activeContext =
        activePanel.params as import('@/entities/tab/model/tab.types').TabContext;
      context.tabId = activeContext.tabId;
    }
    navigateTab(context);
  }, [api, navigateTab]);

  const handleNavigateToCounselList = useCallback(() => {
    const context = deserialize('/workspace/my/counsels');
    const activePanel = api?.activePanel;
    if (activePanel) {
      const activeContext =
        activePanel.params as import('@/entities/tab/model/tab.types').TabContext;
      context.tabId = activeContext.tabId;
    }
    navigateTab(context);
  }, [api, navigateTab]);

  const handleNavigateToCameraSettings = useCallback(() => {
    const context = deserialize('/workspace/settings/camera');
    const activePanel = api?.activePanel;
    if (activePanel) {
      const activeContext =
        activePanel.params as import('@/entities/tab/model/tab.types').TabContext;
      context.tabId = activeContext.tabId;
    }
    navigateTab(context);
  }, [api, navigateTab]);

  // 전역 WorkspaceActionFab 억제 — PlannerPage는 자체 Fab을 포함하므로
  const setSuppressGlobalFab = useSetAtom(suppressWorkspaceActionFabAtom);
  React.useEffect(() => {
    setSuppressGlobalFab(true);
    return () => setSuppressGlobalFab(false);
  }, [setSuppressGlobalFab]);

  // ── Date Navigation ────────────────────────────────────────────────────────
  // 선택 해제는 MapboxMap이 자동 처리(markers가 갱신되면 기존 선택 id가 사라져 자동 unselect).

  const handleScheduleDateChange = useCallback(
    (date: string) => {
      if (props.tabId) updateTabParams(props.tabId, { scheduleDate: date });
    },
    [props.tabId, updateTabParams]
  );

  const handlePrevDay = useCallback(() => {
    const prev = [...workDates]
      .filter((d) => d < scheduleDate)
      .sort()
      .pop();
    handleScheduleDateChange(prev ?? dayjs(scheduleDate).subtract(1, 'day').format('YYYY-MM-DD'));
  }, [scheduleDate, handleScheduleDateChange, workDates]);

  const handleNextDay = useCallback(() => {
    const next = workDates.find((d) => d > scheduleDate);
    handleScheduleDateChange(next ?? dayjs(scheduleDate).add(1, 'day').format('YYYY-MM-DD'));
  }, [scheduleDate, handleScheduleDateChange, workDates]);

  // ── Auth guard ─────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!isAuthInitialized || isAuthLoading) return;
    if (!isPlannerMode || !plannerId) {
      navigateTab(deserialize(`/404?tabId=${props.tabId}`));
    }
  }, [isAuthInitialized, isAuthLoading, isPlannerMode, plannerId, props.tabId, navigateTab]);

  React.useEffect(() => {
    if (!plannerLoading && planner && props.tabId) {
      const updates: Record<string, unknown> = { displayName: planner.name };
      if (props.params?.scheduleDate === undefined) updates.scheduleDate = scheduleDate;
      updateTabParams(props.tabId, updates);
    }
  }, [
    planner,
    plannerLoading,
    props.tabId,
    props.params?.scheduleDate,
    scheduleDate,
    updateTabParams,
  ]);

  // ── Markers & arrows ───────────────────────────────────────────────────────

  const validCounsels = useMemo(
    () => counsels.filter((c) => c.location?.longitude != null && c.location?.latitude != null),
    [counsels]
  );

  const markers = useMemo<MapMarker[]>(
    () =>
      validCounsels.map((c, i) => ({
        id: c.visitCounselId,
        position: { lng: c.location!.longitude, lat: c.location!.latitude },
        badge: String(i + 1),
        color: '#055cf5',
      })),
    [validCounsels]
  );

  const arrows = useMemo<MapArrow[]>(
    () =>
      validCounsels.slice(0, -1).map((c, i) => ({
        from: c.visitCounselId,
        to: validCounsels[i + 1]!.visitCounselId,
        color: '#055cf5',
        width: 3,
        follow: 'road' as const,
      })),
    [validCounsels]
  );

  /**
   * detail 카드의 "상담 시작" / "이어하기" 버튼 클릭 시 호출.
   * referrer에 현재 날짜를 포함해 작업 완료 후 지도 복귀 가능.
   *
   * "상담 시작"/"이어하기" 모두 통합 진입 페이지(`/start` → `CounselStartPage`)로
   * 보낸다. 단계 분기(pick-room / moving-items / done 등)는 `CounselStartPage` 내부의
   * `resolveCounselFlowStage` 가 담당하므로 진입 시 isStarted 분기가 필요 없다.
   */
  const handleNavigateToStartOrProgress = useCallback(
    (counselId: string, _isStarted: boolean) => {
      const referrer = encodeURIComponent(`/workspace/my/planner?scheduleDate=${scheduleDate}`);
      navigateTab(deserialize(`/workspace/my/counsels/${counselId}/start?referrer=${referrer}`));
    },
    [navigateTab, scheduleDate]
  );

  // ── Early returns ──────────────────────────────────────────────────────────

  if (!isAuthInitialized || isAuthLoading) return null;
  if (!isPlannerMode || !plannerId) return null;

  const headerTitle = formatDateTitle(scheduleDate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TabToolbar tabId={props.tabId || ''} />
      <TabAwarePageWrapper>
        <MapProvider accessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN ?? ''}>
          <MapboxSelectionProvider>
            <PlannerMapBody
              isMobile={isMobile}
              headerTitle={headerTitle}
              onPrevDay={handlePrevDay}
              onNextDay={handleNextDay}
              onDateSelect={handleScheduleDateChange}
              scheduleDate={scheduleDate}
              workDates={workDates}
              isWorkDatesLoaded={isWorkDatesLoaded}
              todayDateString={todayDateString}
              todayWorkStatus={todayWorkStatus}
              markers={markers}
              arrows={arrows}
              counsels={counsels}
              onNavigateToStartOrProgress={handleNavigateToStartOrProgress}
              onNavigateToProfile={handleNavigateToProfile}
              onNavigateToCounselList={handleNavigateToCounselList}
              onNavigateToCameraSettings={handleNavigateToCameraSettings}
              initialVisitCounselId={initialVisitCounselId}
              onInitialVisitCounselIdApplied={handleInitialVisitCounselIdApplied}
              initialOpenDetail={initialOpenDetail}
            />
          </MapboxSelectionProvider>
        </MapProvider>
      </TabAwarePageWrapper>
    </Box>
  );
};

export default PlannerPage;
